import { useEffect, useMemo, useState } from "react";
import {
  ADMIN_PASSWORD,
  ADMIN_USERNAME,
  clearAdminSession,
  getAdminSession,
  isAdminCredentials,
  setAdminSession,
} from "../config/adminAuth";
import { adminListOrders, adminUpdateOrderTracking } from "../services/orderPortal";
import { shiprocketAdminRequest } from "../services/shiprocketAdmin";
import { syncShiprocketTracking } from "../services/shiprocketSync";
import { formatInrAmount } from "../utils/formatInrAmount";
import { fetchProductsFromSheet } from "../utils/sheetProducts";
import { parcelFromOrder } from "../utils/parcelFromOrder";
import {
  parseOrderGiftItems,
  productLidPreviewUrls,
} from "../utils/parseOrderGiftItems";
import GiftBoxLidPreview from "../components/GiftBoxLidPreview";
import { giftLidPositionLabel } from "../components/GiftBoxLidEditor";
import { BtnContent } from "../components/BtnIcon";
import {
  IoLogInOutline,
  IoLogOutOutline,
  IoPrintOutline,
  IoRefreshOutline,
  IoSyncOutline,
  IoAddCircleOutline,
  IoPricetagOutline,
  IoTrashOutline,
} from "react-icons/io5";
import "../styles/admin.css";
import "../styles/gift-customise.css";

const SHIPMENT_DEFAULTS = {
  pickupLocation: "Primary",
  senderPincode: "401107",
  city: "",
  state: "",
  country: "India",
  paymentMethod: "Prepaid",
};

function getField(row, ...keys) {
  for (const key of keys) {
    const normalized = key.replace(/\s+/g, "").toLowerCase();
    if (row?.[normalized] != null && row[normalized] !== "") return row[normalized];
  }
  return "";
}

function parcelForOrder(order, baseParcel, products) {
  return parcelFromOrder(order, baseParcel, products);
}

function formatOrderDateTime(value) {
  if (value == null || value === "") return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function OrderItemsCell({ itemsText, products }) {
  const text = String(itemsText || "").trim();
  if (!text) return <span className="text-muted">—</span>;

  const items = parseOrderGiftItems(text);
  const byId = new Map((products || []).map((p) => [String(p.id), p]));

  if (!items.length) {
    return (
      <div className="admin-order-items">
        <div className="admin-order-items__line">{text}</div>
      </div>
    );
  }

  return (
    <div className="admin-order-items">
      {items.map((item, index) => {
        const product = byId.get(String(item.id));
        const hasGift = Boolean(item.giftLidMessage);
        const scale = item.giftLidLayout ? Number(item.giftLidLayout.scale).toFixed(2) : null;

        return (
          <div key={`${index}-${item.id}-${item.giftLidMessage || item.raw}`} className="admin-order-items__block">
            <div className="admin-order-items__line">
              {item.name}
              {item.id ? ` (ID: ${item.id})` : ""}
              {item.quantity > 1 ? ` × ${item.quantity}` : ""}
            </div>

            {hasGift ? (
              <div className="admin-gift-lid-preview">
                <GiftBoxLidPreview
                  imageUrls={productLidPreviewUrls(product)}
                  message={item.giftLidMessage}
                  layout={item.giftLidLayout}
                  textColor={item.giftLidTextColor || "gold"}
                />
                <p className="admin-gift-lid-preview__meta small text-muted mb-0">
                  Message: {item.giftLidMessage}
                  {item.giftLidLayout ? (
                    <>
                      {" "}
                      · {giftLidPositionLabel(item.giftLidLayout)}
                      {scale ? ` · ${scale}×` : ""}
                    </>
                  ) : null}
                  {item.giftLidTextColor ? ` · ${item.giftLidTextColor} text` : null}
                </p>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** Checkout-style totals: items, shipping rate/courier, discounts, total. */
function OrderPricingCell({ order }) {
  const subtotal = Number(getField(order, "subtotal")) || 0;
  const shipping = Number(getField(order, "shipping")) || 0;
  const total = Number(getField(order, "total")) || 0;
  const courier = getField(order, "shipping courier", "shippingcourier");
  const etd = getField(order, "shipping etd", "shippingetd");
  const couponCode = getField(order, "coupon code", "couponcode");
  const couponDiscount = Number(getField(order, "coupon discount", "coupondiscount")) || 0;
  const payment = getField(order, "payment method", "paymentmethod");
  const profit = String(getField(order, "profit") || "");
  const loyaltyMatch = profit.match(/LOYALTY_VOUCHER_(\d+)/i);
  const loyaltyDiscount = loyaltyMatch ? Number(loyaltyMatch[1]) || 0 : 0;

  const row = (label, amount, { accent, muted } = {}) => (
    <div
      className={`admin-order-pricing__row${muted ? " text-muted" : ""}${accent ? " text-success" : ""}`}
    >
      <span>{label}</span>
      <span className="price-figure">{amount}</span>
    </div>
  );

  return (
    <div className="admin-order-pricing">
      {loyaltyDiscount > 0 &&
        row("Loyalty voucher", `−₹${formatInrAmount(loyaltyDiscount)}`, { accent: true })}
      {couponDiscount > 0 &&
        row(
          couponCode ? `Coupon (${couponCode})` : "Coupon discount",
          `−₹${formatInrAmount(couponDiscount)}`,
          { accent: true },
        )}
      {row("Items subtotal", `₹${formatInrAmount(subtotal)}`)}
      <div className="admin-order-pricing__row">
        <span>
          Shipping
          {courier ? ` (${courier})` : ""}
        </span>
        <span className="price-figure">₹{formatInrAmount(shipping)}</span>
      </div>
      {etd ? <div className="admin-order-pricing__etd text-muted">Est. delivery: {etd}</div> : null}
      <div className="admin-order-pricing__row admin-order-pricing__row--total">
        <span>Total</span>
        <span className="price-figure price-figure--emphasis">₹{formatInrAmount(total)}</span>
      </div>
      {payment ? <div className="admin-order-pricing__payment text-muted">{payment}</div> : null}
    </div>
  );
}

function Admin() {
  const [session, setSession] = useState(() => getAdminSession());
  const [login, setLogin] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [parcel, setParcel] = useState(SHIPMENT_DEFAULTS);
  const [products, setProducts] = useState([]);
  const [busyOrderId, setBusyOrderId] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [rateResults, setRateResults] = useState({});
  const [rateLoadingOrderId, setRateLoadingOrderId] = useState("");
  const [selectedCouriers, setSelectedCouriers] = useState({});

  const adminCredentials = useMemo(
    () => ({ username: ADMIN_USERNAME, password: ADMIN_PASSWORD }),
    [],
  );

  const loadOrders = async () => {
    setLoading(true);
    setMessage("");
    const [res, productList] = await Promise.all([
      adminListOrders(adminCredentials),
      fetchProductsFromSheet().catch(() => []),
    ]);
    setLoading(false);
    setProducts(Array.isArray(productList) ? productList : []);
    if (!res.ok) {
      setMessage(res.message || "Could not load orders.");
      setOrders([]);
      return;
    }
    setOrders(Array.isArray(res.data?.orders) ? res.data.orders : []);
  };

  useEffect(() => {
    if (session) loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const submitLogin = (e) => {
    e.preventDefault();
    setLoginError("");
    if (!isAdminCredentials(login.username, login.password)) {
      setLoginError("Invalid admin username or password.");
      return;
    }
    setAdminSession();
    setSession(getAdminSession());
  };

  const logout = () => {
    clearAdminSession();
    setSession(null);
    setOrders([]);
  };

  const runManualSync = async () => {
    setSyncing(true);
    setMessage("Syncing Shiprocket tracking...");
    const res = await syncShiprocketTracking();
    setSyncing(false);
    if (!res.ok) {
      setMessage(res.message || "Shiprocket sync failed.");
      return;
    }
    const checked = res.data?.checked ?? 0;
    const totalOrders = res.data?.totalOrders ?? orders.length;
    const failed = Array.isArray(res.data?.results)
      ? res.data.results.filter((row) => !row.ok).length
      : 0;
    setMessage(
      failed
        ? `Shiprocket sync checked ${checked} of ${totalOrders} orders; ${failed} failed.`
        : `Shiprocket sync complete. Checked ${checked} of ${totalOrders} orders.`,
    );
    loadOrders();
  };

  const updateTracking = async (order, patch) => {
    return adminUpdateOrderTracking({
      ...adminCredentials,
      orderId: getField(order, "order id", "orderid"),
      ...patch,
    });
  };

  const createShipment = async (order) => {
    const orderId = String(getField(order, "order id", "orderid") || "").trim();
    const selectedCourier =
      selectedCouriers[orderId] ||
      (() => {
        const savedCourierId = String(
          getField(order, "courier company id", "couriercompanyid", "shipping courier id") || "",
        ).trim();
        const savedCourierName = String(
          getField(order, "shipping courier", "shippingcourier") || "",
        ).trim();
        if (!savedCourierId && !savedCourierName) return null;
        return {
          courierCompanyId: savedCourierId,
          courierName: savedCourierName || "Customer choice",
        };
      })();
    if (!selectedCourier?.courierCompanyId) {
      setMessage("Select a courier/rate option before creating shipment.");
      return;
    }
    setBusyOrderId(orderId);
    setMessage("");
    const res = await shiprocketAdminRequest({
      ...adminCredentials,
      action: "createOrder",
      order,
      parcel: parcelForOrder(order, parcel, products),
      courierCompanyId: selectedCourier.courierCompanyId,
      selectedCourier,
    });
    if (!res.ok) {
      setMessage(res.message || "Shiprocket order creation failed.");
      setBusyOrderId("");
      return;
    }
    const summary = res.data?.summary || {};
    const save = await updateTracking(order, {
      awb: summary.awb,
      shiprocketOrderId: summary.shiprocketOrderId,
      shipmentId: summary.shipmentId,
      trackingUrl: summary.trackingUrl,
      trackingStatus: summary.trackingStatus || summary.status || "Shipment created",
      trackingDetails: res.data,
    });
    setMessage(save.ok ? `Shipment created for ${orderId}.` : save.message || "Shipment created, but sheet update failed.");
    setBusyOrderId("");
    loadOrders();
  };

  const checkRates = async (order) => {
    const orderId = String(getField(order, "order id", "orderid") || "").trim();
    const receiverPincode = String(getField(order, "pincode") || "").trim();
    if (!receiverPincode) {
      setMessage("Receiver pincode missing for this order.");
      return;
    }
    setRateLoadingOrderId(orderId);
    setMessage("");
    const res = await shiprocketAdminRequest({
      ...adminCredentials,
      action: "getRates",
      order,
      parcel: parcelForOrder(order, parcel, products),
      deliveryPostcode: receiverPincode,
    });
    setRateLoadingOrderId("");
    if (!res.ok) {
      setMessage(res.message || "Could not load Shiprocket rates.");
      return;
    }
    setRateResults((current) => ({
      ...current,
      [orderId]: {
        senderPincode: res.data?.senderPincode || parcel.senderPincode,
        receiverPincode: res.data?.receiverPincode || receiverPincode,
        options: res.data?.summary?.options || [],
      },
    }));
    const firstOption = res.data?.summary?.options?.[0];
    const savedCourierId = String(
      getField(order, "courier company id", "couriercompanyid", "shipping courier id") || "",
    ).trim();
    const savedCourierName = String(getField(order, "shipping courier", "shippingcourier") || "").trim();
    const options = res.data?.summary?.options || [];
    const matched =
      (savedCourierId &&
        options.find((o) => String(o.courierCompanyId) === savedCourierId)) ||
      (savedCourierName &&
        options.find(
          (o) => String(o.courierName).toLowerCase() === savedCourierName.toLowerCase(),
        )) ||
      firstOption;
    if (matched?.courierCompanyId) {
      setSelectedCouriers((current) => ({
        ...current,
        [orderId]: matched,
      }));
    }
  };

  const generateLabel = async (order) => {
    const orderId = String(getField(order, "order id", "orderid") || "").trim();
    const shipmentId = String(getField(order, "shiprocket shipment id", "shiprocketshipmentid") || "").trim();
    if (!shipmentId) {
      setMessage("Shipment ID missing. Create Shiprocket shipment first.");
      return;
    }
    setBusyOrderId(orderId);
    const res = await shiprocketAdminRequest({
      ...adminCredentials,
      action: "generateLabel",
      shipmentId,
    });
    if (!res.ok) {
      setMessage(res.message || "Could not generate label.");
      setBusyOrderId("");
      return;
    }
    const labelUrl = res.data?.summary?.labelUrl || "";
    await updateTracking(order, {
      shipmentId,
      labelUrl,
      trackingStatus: res.data?.summary?.status || "Label generated",
      trackingDetails: res.data,
    });
    if (labelUrl) window.open(labelUrl, "_blank", "noopener,noreferrer");
    setMessage(labelUrl ? `Label generated for ${orderId}.` : "Label generated, but no URL returned.");
    setBusyOrderId("");
    loadOrders();
  };

  const cancelShipment = async (order) => {
    const orderId = String(getField(order, "order id", "orderid") || "").trim();
    const awb = String(getField(order, "awb no", "awb", "awb code", "awbcode") || "").trim();
    const shiprocketOrderId = String(getField(order, "shiprocket order id", "shiprocketorderid") || "").trim();
    if (!awb && !shiprocketOrderId) {
      setMessage("AWB or Shiprocket order ID missing.");
      return;
    }
    setBusyOrderId(orderId);
    const res = await shiprocketAdminRequest({
      ...adminCredentials,
      action: "cancelShipment",
      awb,
      shiprocketOrderId,
    });
    await updateTracking(order, {
      awb,
      shiprocketOrderId,
      trackingStatus: res.ok ? "Shipment cancelled" : "Shipment cancellation failed",
      trackingDetails: res.data || res,
    });
    setMessage(res.ok ? `Shipment cancelled for ${orderId}.` : res.message || "Cancellation failed.");
    setBusyOrderId("");
    loadOrders();
  };

  if (!session) {
    return (
      <div className="admin-page container py-5" style={{ maxWidth: 480 }}>
        <h1 className="h3 mb-3">Admin login</h1>
        <form onSubmit={submitLogin} className="card border-0 shadow-sm p-4">
          <label className="form-label">Username</label>
          <input
            className="form-control mb-3"
            value={login.username}
            onChange={(e) => setLogin((v) => ({ ...v, username: e.target.value }))}
          />
          <label className="form-label">Password</label>
          <input
            type="password"
            className="form-control mb-3"
            value={login.password}
            onChange={(e) => setLogin((v) => ({ ...v, password: e.target.value }))}
          />
          {loginError && <p className="small text-danger">{loginError}</p>}
          <button className="btn btn-primary" type="submit">
            <BtnContent icon={IoLogInOutline}>Log in</BtnContent>
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-page container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h1 className="h3 mb-1">Admin orders</h1>
          <p className="text-muted small mb-0">Create Shiprocket shipments, print labels, cancel shipments, and update AWB tracking.</p>
        </div>
        <div className="d-flex flex-wrap admin-toolbar">
          <button className="btn btn-outline-secondary btn-sm" type="button" onClick={loadOrders}>
            <BtnContent icon={IoRefreshOutline}>Refresh</BtnContent>
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            type="button"
            onClick={runManualSync}
            disabled={syncing}
          >
            <BtnContent icon={IoSyncOutline}>{syncing ? "Syncing…" : "Sync Shiprocket"}</BtnContent>
          </button>
          <button className="btn btn-outline-danger btn-sm" type="button" onClick={logout}>
            <BtnContent icon={IoLogOutOutline}>Log out</BtnContent>
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <h2 className="h6 mb-3">Shipment defaults</h2>
          <p className="small text-muted mb-3">
            Sender pincode is fixed as <strong>401107</strong>. Parcel size and weight are calculated
            from each order&apos;s items (collapsed packaging rules). Rates use the customer&apos;s pincode.
          </p>
          <div className="row g-2">
            {Object.keys(SHIPMENT_DEFAULTS).map((key) => (
              <div className="col-6 col-md-3" key={key}>
                <label className="form-label small text-capitalize">{key.replace(/([A-Z])/g, " $1")}</label>
                <input
                  className="form-control form-control-sm"
                  value={parcel[key]}
                  disabled={key === "senderPincode"}
                  onChange={(e) => setParcel((v) => ({ ...v, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {message && <div className="alert alert-info small">{message}</div>}
      {loading ? (
        <p className="text-muted">Loading orders...</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle admin-orders-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Order total</th>
                <th>Tracking</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, idx) => {
                const orderId = String(getField(order, "order id", "orderid") || idx);
                const orderDate = formatOrderDateTime(getField(order, "timestamp"));
                const shipmentId = getField(order, "shiprocket shipment id", "shiprocketshipmentid");
                const awb = getField(order, "awb no", "awb", "awb code", "awbcode");
                const labelUrl = getField(order, "shipping label url", "shippinglabelurl");
                const busy = busyOrderId === orderId;
                const rateInfo = rateResults[orderId];
                const ratesBusy = rateLoadingOrderId === orderId;
                return (
                  <tr key={`${orderId}-${idx}`}>
                    <td className="small font-monospace">{orderId}</td>
                    <td className="small text-muted admin-order-date text-nowrap">{orderDate}</td>
                    <td className="small">
                      <div>{getField(order, "name") || "—"}</div>
                      <div className="text-muted">{getField(order, "email")}</div>
                      <div className="text-muted">{getField(order, "phone")}</div>
                      <div className="text-muted">{getField(order, "address")}</div>
                      <div className="text-muted">{getField(order, "pincode")}</div>
                    </td>
                    <td className="small">
                      <OrderItemsCell itemsText={getField(order, "items")} products={products} />
                    </td>
                    <td className="small">
                      <OrderPricingCell order={order} />
                    </td>
                    <td className="small">
                      <div>AWB: {awb || "—"}</div>
                      <div>Shipment: {shipmentId || "—"}</div>
                      <div>{getField(order, "tracking status", "trackingstatus") || "—"}</div>
                      {labelUrl && <a href={labelUrl} target="_blank" rel="noreferrer">Label</a>}
                    </td>
                    <td className="small">
                      <div className="admin-actions">
                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => checkRates(order)} disabled={ratesBusy}>
                          <BtnContent icon={IoPricetagOutline}>{ratesBusy ? "Checking…" : "Rates"}</BtnContent>
                        </button>
                        <button className="btn btn-primary btn-sm" type="button" onClick={() => createShipment(order)} disabled={busy}>
                          <BtnContent icon={IoAddCircleOutline}>Create</BtnContent>
                        </button>
                        <button className="btn btn-outline-primary btn-sm" type="button" onClick={() => generateLabel(order)} disabled={busy || !shipmentId}>
                          <BtnContent icon={IoPrintOutline}>Label</BtnContent>
                        </button>
                        <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => cancelShipment(order)} disabled={busy || (!awb && !getField(order, "shiprocket order id", "shiprocketorderid"))}>
                          <BtnContent icon={IoTrashOutline}>Cancel</BtnContent>
                        </button>
                      </div>
                      {rateInfo && (
                        <div className="mt-2 border rounded p-2 bg-light admin-rates-panel">
                          <div className="fw-semibold mb-1">
                            Rates: {rateInfo.senderPincode} → {rateInfo.receiverPincode}
                          </div>
                          {rateInfo.options.length === 0 ? (
                            <div className="text-muted">No courier options returned.</div>
                          ) : (
                            <div className="d-grid gap-1">
                              {rateInfo.options.slice(0, 5).map((option, optionIndex) => (
                                <label
                                  key={`${option.courierCompanyId}-${optionIndex}`}
                                  className="d-flex justify-content-between gap-2 align-items-start"
                                  style={{ cursor: "pointer" }}
                                >
                                  <span className="d-flex gap-1">
                                    <input
                                      type="radio"
                                      name={`courier-${orderId}`}
                                      checked={selectedCouriers[orderId]?.courierCompanyId === option.courierCompanyId}
                                      onChange={() =>
                                        setSelectedCouriers((current) => ({
                                          ...current,
                                          [orderId]: option,
                                        }))
                                      }
                                    />
                                    <span>{option.courierName}</span>
                                  </span>
                                  <span className="fw-semibold text-end">
                                    {option.rate !== "" ? `₹${formatInrAmount(option.rate)}` : "—"}
                                    {option.etd ? ` · ${option.etd}` : ""}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                          {selectedCouriers[orderId]?.courierName && (
                            <div className="mt-2 text-success">
                              Selected: {selectedCouriers[orderId].courierName}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {orders.length === 0 && (
                <tr><td colSpan="7" className="text-muted">No orders found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Admin;
